import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { createApiClient } from '@/services/api';
import { useAuth } from '@clerk/clerk-react';
import { Check, Clock, Loader2, MapPin, Phone, UserPlus, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface AgentLead {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  territory: string;
  territory_name: string | null;
  experience: string | null;
  current_business: string | null;
  gym_connections: string | null;
  message: string | null;
  status: string;
  notes: string | null;
  on_waiting_list: number;
  created_at: string;
}

export default function AdminAgentLeads() {
  const { getToken } = useAuth();
  const client = useMemo(() => createApiClient(getToken), [getToken]);
  const [leads, setLeads] = useState<AgentLead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'converted' | 'closed'>('pending');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchLeads = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await client.get<{ leads: AgentLead[]; total: number }>(
        `/admin/agent-leads?status=${filter}`
      );
      if (response.success && response.data) {
        setLeads(response.data.leads);
      }
    } catch (err) {
      console.error('Failed to fetch agent leads:', err);
      toast.error('Failed to load agent leads');
    } finally {
      setIsLoading(false);
    }
  }, [client, filter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleApprove = async (leadId: string) => {
    setActionLoading(leadId);
    try {
      const response = await client.post<{ user_id: string; agent_id: string }>(
        `/admin/agent-leads/${leadId}/approve`,
        {}
      );
      if (response.success) {
        toast.success('Agent lead approved — user and agent record created');
        fetchLeads();
      } else {
        toast.error(response.error || 'Failed to approve');
      }
    } catch (err) {
      toast.error('Failed to approve agent lead');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (leadId: string) => {
    setActionLoading(leadId);
    try {
      const response = await client.post(`/admin/agent-leads/${leadId}/reject`, {});
      if (response.success) {
        toast.success('Agent lead closed');
        fetchLeads();
      } else {
        toast.error(response.error || 'Failed to reject');
      }
    } catch (err) {
      toast.error('Failed to reject agent lead');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <Badge variant="default">New</Badge>;
      case 'contacted':
        return <Badge variant="secondary">Contacted</Badge>;
      case 'qualified':
        return <Badge variant="secondary">Qualified</Badge>;
      case 'converted':
        return (
          <Badge variant="default" className="bg-green-600">
            Converted
          </Badge>
        );
      case 'closed':
        return <Badge variant="destructive">Closed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Agent Enquiries</h1>
            <p className="text-muted-foreground">Review and manage agent territory enquiries</p>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2">
          {(['pending', 'all', 'converted', 'closed'] as const).map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
            >
              {f === 'pending'
                ? 'Pending'
                : f === 'all'
                  ? 'All'
                  : f === 'converted'
                    ? 'Approved'
                    : 'Closed'}
            </Button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : leads.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <UserPlus className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No agent enquiries {filter !== 'all' ? `with status "${filter}"` : ''}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {leads.map((lead) => (
              <Card key={lead.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{lead.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{lead.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(lead.status)}
                      {lead.on_waiting_list === 1 && (
                        <Badge variant="outline">
                          <Clock className="h-3 w-3 mr-1" />
                          Waiting List
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2 text-sm">
                    {lead.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        {lead.phone}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      {lead.territory_name || lead.territory}
                    </div>
                  </div>

                  {lead.experience && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase mb-1">
                        Experience
                      </p>
                      <p className="text-sm">{lead.experience}</p>
                    </div>
                  )}

                  {lead.current_business && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase mb-1">
                        Current Business
                      </p>
                      <p className="text-sm">{lead.current_business}</p>
                    </div>
                  )}

                  {lead.gym_connections && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase mb-1">
                        Gym Connections
                      </p>
                      <p className="text-sm">{lead.gym_connections}</p>
                    </div>
                  )}

                  {lead.message && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase mb-1">
                        Message
                      </p>
                      <p className="text-sm">{lead.message}</p>
                    </div>
                  )}

                  <div className="text-xs text-muted-foreground">
                    Submitted{' '}
                    {new Date(lead.created_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </div>

                  {/* Actions for pending leads */}
                  {['new', 'contacted', 'qualified'].includes(lead.status) && (
                    <div className="flex gap-2 pt-2 border-t border-border">
                      <Button
                        size="sm"
                        onClick={() => handleApprove(lead.id)}
                        disabled={actionLoading === lead.id}
                      >
                        {actionLoading === lead.id ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4 mr-1" />
                        )}
                        Approve & Create Agent
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReject(lead.id)}
                        disabled={actionLoading === lead.id}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Close
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
